import { useParams, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Megaphone, ArrowLeft, Loader2, Target, Calendar, Users, BarChart3, QrCode, XCircle, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DataPagination } from "@/components/shared/DataPagination";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useCampaign, useCampaignConversions, useCloseCampaign, useDeleteCampaign } from "@/hooks/useCampaigns";
import { useCompany } from "@/hooks/useCompanies";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { toast } from "sonner";

export default function CampaignDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [conversionsPage, setConversionsPage] = useState(1);
  const { data: campaign, isLoading: loadingCampaign } = useCampaign(id);
  const { data: conversionsData, isLoading: loadingConversions } = useCampaignConversions(id, conversionsPage);
  const conversions = conversionsData?.items ?? [];
  const { data: company } = useCompany(campaign?.company_id.toString());
  const { data: currentUser } = useCurrentUser();
  const closeCampaign = useCloseCampaign();
  const deleteCampaign = useDeleteCampaign();
  const isAdmin = currentUser?.roles?.includes("indiqr-admin");

  function handleClose() {
    if (!confirm("Encerrar esta campanha? Esta ação não pode ser desfeita.")) return;
    closeCampaign.mutate(id!, {
      onSuccess: () => {
        toast.success("Campanha encerrada.");
        navigate("/campanhas");
      },
      onError: (err: unknown) => {
        const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? "Erro ao encerrar campanha";
        toast.error(msg);
      },
    });
  }

  function handleDelete() {
    deleteCampaign.mutate(id!, {
      onSuccess: () => {
        toast.success("Campanha deletada permanentemente.");
        navigate("/campanhas");
      },
      onError: (err: unknown) => {
        const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? "Erro ao deletar campanha";
        toast.error(msg);
      },
    });
  }

  if (loadingCampaign || loadingConversions) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="text-center py-10">
        <h2 className="text-xl font-semibold">Campanha não encontrada</h2>
        <Button variant="link" onClick={() => navigate("/campanhas")}>Voltar para a lista</Button>
      </div>
    );
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "ativa":
        return <Badge className="bg-green-500">Ativa</Badge>;
      case "aguardando_aceite":
        return <Badge variant="secondary">Aguardando Aceite</Badge>;
      case "encerrada":
        return <Badge variant="destructive">Encerrada</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const totalComissao = conversions?.reduce((acc, c) => acc + Number(c.remuneracao_valor), 0) || 0;
  const totalVendas = conversions?.reduce((acc, c) => acc + Number(c.valor_bruto), 0) || 0;

  const usaPontos = campaign.usa_pontos;
  function fmtValor(n: number) {
    return usaPontos
      ? `${Math.round(n)} pts`
      : n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  }

  return (
    <div className="space-y-6">
      <Button
        variant="ghost"
        className="gap-2 -ml-2 text-muted-foreground"
        onClick={() => navigate("/campanhas")}
      >
        <ArrowLeft className="h-4 w-4" />
        Voltar
      </Button>

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-primary/10 rounded-xl text-primary">
            <Megaphone className="h-6 w-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold tracking-tight text-foreground">{campaign.name}</h1>
              {getStatusBadge(campaign.status)}
            </div>
            <p className="text-muted-foreground">Empresa: {company?.name || "Carregando..."}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isAdmin && campaign.status !== "encerrada" && (
            <Button
              variant="destructive"
              className="gap-2"
              onClick={handleClose}
              disabled={closeCampaign.isPending}
            >
              {closeCampaign.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
              Encerrar Campanha
            </Button>
          )}

          {isAdmin && campaign.status === "encerrada" && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" className="gap-2" disabled={deleteCampaign.isPending}>
                  <Trash2 className="h-4 w-4" />
                  Deletar Campanha
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Deletar campanha permanentemente?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Esta ação não pode ser desfeita. A campanha será removida do sistema
                    e o indicador receberá uma notificação por email.
                    O QRCode já está inativo e permanecerá assim.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    disabled={deleteCampaign.isPending}
                  >
                    {deleteCampaign.isPending ? "Deletando..." : "Sim, deletar"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="text-xs uppercase font-semibold">Conversões</CardDescription>
            <CardTitle className="text-2xl">{conversionsData?.total || 0}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <BarChart3 className="h-3 w-3" />
              Total validado
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="text-xs uppercase font-semibold">Total em Vendas</CardDescription>
            <CardTitle className="text-2xl">{fmtValor(totalVendas)}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <Target className="h-3 w-3" />
              Bruto gerado
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="text-xs uppercase font-semibold">Comissão Total</CardDescription>
            <CardTitle className="text-2xl">{fmtValor(totalComissao)}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <Users className="h-3 w-3" />
              A pagar ao indicador
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="text-xs uppercase font-semibold">Remuneração</CardDescription>
            <CardTitle className="text-xl">
              {campaign.remuneracao_modelo === "percentual"
                ? `${campaign.remuneracao_valor}%`
                : fmtValor(Number(campaign.remuneracao_valor))}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xs text-muted-foreground">
              Modelo: {campaign.remuneracao_modelo}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg text-primary flex items-center gap-2">
              <QrCode className="h-5 w-5" />
              Histórico de Conversões
            </CardTitle>
            <CardDescription>Últimas validações realizadas por vendedores.</CardDescription>
          </CardHeader>
          <CardContent>
            {conversions.length > 0 ? (
              <>
                <div className="space-y-4">
                  {conversions.map((conv) => (
                    <div key={conv.id} className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                      <div>
                        <p className="text-sm font-medium">Venda de {fmtValor(Number(conv.valor_bruto))}</p>
                        <p className="text-xs text-muted-foreground">
                          Comissão: {fmtValor(Number(conv.remuneracao_valor))}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground flex items-center gap-1 justify-end">
                          <Calendar className="h-3 w-3" />
                          {new Date(conv.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4">
                  <DataPagination
                    page={conversionsData?.page ?? 1}
                    pages={conversionsData?.pages ?? 1}
                    onPageChange={setConversionsPage}
                  />
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8 italic">Nenhuma conversão registrada ainda.</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Detalhes da Campanha</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground uppercase font-semibold tracking-wider">Desconto Cliente</p>
              <p className="text-sm">{campaign.desconto_pct ? `${campaign.desconto_pct}%` : "Nenhum"}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground uppercase font-semibold tracking-wider">Data de Criação</p>
              <p className="text-sm">{new Date(campaign.created_at).toLocaleDateString()}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground uppercase font-semibold tracking-wider">ID da Campanha</p>
              <p className="text-xs font-mono break-all">{campaign.id}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
